import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  DateCalendar,
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { Box, Popper } from "@mui/material";
import { StyleSettings } from "../../styles/StyleSettings";

interface SnPeriodMonthBoxProps {
  defaultValue?: string;
  width?: number;
  onValueChanged?: (start: string, end: string) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
}

const SnPeriodMonthBox = React.memo(
  ({
    defaultValue,
    width,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    onFocus,
  }: SnPeriodMonthBoxProps) => {
    const start: string = defaultValue ? defaultValue.split(",")[0].trim() : "";
    const end: string = defaultValue ? defaultValue.split(",")[1].trim() : "";
    const [value, setValue] = useState<[Dayjs | null, Dayjs | null]>([
      dayjs(start || new Date()),
      dayjs(end || new Date()),
    ]);
    const [isPopperOpen, setIsPopperOpen] = useState(false);
    const divRef = useRef<HTMLDivElement | null>(null);
    const secondRef = useRef<HTMLDivElement | null>(null);
    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");

    const fetchDefaultValue = async () => {
      try {
        if (defaultValue !== undefined) {
          const start: string = defaultValue.split(",")[0].trim();
          const end: string = defaultValue.split(",")[1].trim();
          const initialValue: [Dayjs, Dayjs] = [
            dayjs(start || new Date()),
            dayjs(end || new Date()),
          ];
          setValue(initialValue);
        }
      } catch (error: any) {}
    };
    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    const fetchIsEditable = async () => {
      try {
        if (isEditable) setBackgroundColor("white");
        else setBackgroundColor("#F1F1F1");
      } catch (error: any) {}
    };
    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      fetchIsEditable();
    }, [isEditable]);

    // 외부 클릭 감지
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          divRef.current &&
          !divRef.current.contains(event.target as Node) &&
          !document
            .getElementById("calendar-popper")
            ?.contains(event.target as Node)
        ) {
          handleClose();
        }
      };

      if (isPopperOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isPopperOpen]);

    const handleOpen = () => {
      if (!isReadOnly) {
        setIsPopperOpen(true);
      }
    };

    const handleClose = () => {
      setIsPopperOpen(false);
      secondRef.current?.focus();
    };

    const returnData = (startDate: Dayjs | null, endDate: Dayjs | null) => {
      let start: string = "";
      let end: string = "";
      if (startDate !== null) start = startDate.format("YYYY-MM");
      if (endDate !== null) end = endDate.format("YYYY-MM");
      return { start, end };
    };

    const emitChange = (startDate: Dayjs | null, endDate: Dayjs | null) => {
      setValue([startDate, endDate]);
      const date = returnData(startDate, endDate);
      if (onValueChanged) {
        onValueChanged(
          date.start.replace(/-/g, ""),
          date.end.replace(/-/g, "")
        );
      }
    };

    // 텍스트 입력: 기존과 동일하게 반대편 날짜를 건드리지 않음
    const handleStartDateChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      emitChange(newValue, value[1]);
    };

    const handleEndDateChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      emitChange(value[0], newValue);
      handleClose();
    };

    // 달력 클릭: 시작일이 종료일보다 뒤면 종료일을 시작일과 같은 날로 맞춤
    const handleStartCalendarChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      const endDate =
        value[1] && newValue.isAfter(value[1], "month")
          ? newValue
          : value[1];
      emitChange(newValue, endDate);
    };

    // 달력 클릭: 종료일이 시작일보다 앞이면 시작일을 종료일과 같은 날로 맞춤
    const handleEndCalendarChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      const startDate =
        value[0] && newValue.isBefore(value[0], "month")
          ? newValue
          : value[0];
      emitChange(startDate, newValue);
      handleClose();
    };

    // 날짜 더블클릭 시 Popper 닫기
    const handleDateDoubleClick = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      handleClose();
    };

    const handleKeyDown1 = async (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        if (isSearchPanel) {
          onFocus && onFocus("current");
        } else {
          secondRef.current?.focus();
        }
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        secondRef.current?.focus();
      }
    };

    const handleKeyDown2 = async (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        if (isSearchPanel) {
          e.preventDefault();
          setIsPopperOpen(false);
          if (isSearchPanel) {
            secondRef.current?.focus();
          } else {
            onFocus && onFocus("next");
          }
        } else {
          e.preventDefault();
          setIsPopperOpen(false);
          onFocus && onFocus("next");
        }
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        setIsPopperOpen(false);
        e.preventDefault();
        onFocus && onFocus("next");
      }
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
        <div
          ref={divRef}
          style={{ display: "flex", alignItems: "center", gap: "15px" }}
        >
          <div style={{ width: "100%" }}>
            <DatePicker
              value={value[0]}
              format={"YYYY/MM"}
              // views={["year", "month"]}
              // openTo={"month"}
              onChange={handleStartDateChange}
              disabled={isReadOnly}
              slotProps={{
                textField: {
                  onClick: handleOpen,
                  onKeyDown: handleKeyDown1,
                  inputRef: inputRef,
                  fullWidth: true,
                  sx: {
                    "& .MuiInputBase-root": {
                      height: StyleSettings.control_height,
                      fontSize: StyleSettings.font,
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderWidth: borderWidth, // 테두리 두께 설정
                      },
                      "&:hover fieldset": {
                        borderWidth: borderWidth, // 호버 시 두께 유지
                      },
                      "&.Mui-focused fieldset": {
                        borderWidth: borderWidth, // 포커스 시 두께 유지
                      },
                    },
                    backgroundColor: backgroundColor,
                  },
                  InputProps: {
                    endAdornment: null, // 달력 아이콘 제거
                  },
                },
              }}
            />
          </div>
          <Box> – </Box>
          <div style={{ width: "100%" }}>
            <DatePicker
              value={value[1]}
              format={"YYYY/MM"}
              // views={["year", "month"]}
              // openTo={"month"}
              onChange={handleEndDateChange}
              disabled={isReadOnly}
              slotProps={{
                textField: {
                  onClick: handleOpen,
                  onKeyDown: handleKeyDown2,
                  fullWidth: true,
                  inputRef: secondRef,
                  sx: {
                    "& .MuiInputBase-root": {
                      height: StyleSettings.control_height,
                      fontSize: StyleSettings.font,
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderWidth: borderWidth, // 테두리 두께 설정
                      },
                      "&:hover fieldset": {
                        borderWidth: borderWidth, // 호버 시 두께 유지
                      },
                      "&.Mui-focused fieldset": {
                        borderWidth: borderWidth, // 포커스 시 두께 유지
                      },
                    },
                    backgroundColor: backgroundColor,
                  },
                  InputProps: {
                    endAdornment: null, // 달력 아이콘 제거
                  },
                },
              }}
            />
          </div>
        </div>
        <Popper
          id="calendar-popper"
          open={isPopperOpen}
          anchorEl={divRef.current}
          placement="bottom-start"
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 0],
              },
            },
          ]}
          sx={{
            boxShadow:
              "0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
            borderRadius: "8px",
            backgroundColor: "white",
            zIndex: 1500,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              p: 2,
              borderRadius: "8px",
              backgroundColor: "white",
              "& .MuiDateCalendar-root": {
                height: "auto",
              },
              "& .MuiMonthCalendar-root": {
                width: "auto",
              },
              "& .MuiPickersCalendarHeader-root": {
                marginTop: 0,
              },
            }}
          >
            <DateCalendar
              value={value[0]}
              onChange={handleStartCalendarChange}
              views={["year", "month"]}
              openTo={"month"}
              sx={{
                width: 300,
                backgroundColor: backgroundColor,
                "& .MuiPickersSlideTransition-root": {
                  minHeight: "205px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: borderWidth, // 테두리 두께 설정
                  },
                  "&:hover fieldset": {
                    borderWidth: borderWidth, // 호버 시 두께 유지
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: borderWidth, // 포커스 시 두께 유지
                  },
                },
              }}
              slotProps={{
                monthButton: {
                  onDoubleClickCapture: (event) => handleDateDoubleClick(event),
                },
              }}
            />
            <DateCalendar
              value={value[1]}
              onChange={handleEndCalendarChange}
              views={["year", "month"]}
              openTo={"month"}
              sx={{
                width: 300,
                backgroundColor: backgroundColor,
                "& .MuiPickersSlideTransition-root": {
                  minHeight: "205px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: borderWidth, // 테두리 두께 설정
                  },
                  "&:hover fieldset": {
                    borderWidth: borderWidth, // 호버 시 두께 유지
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: borderWidth, // 포커스 시 두께 유지
                  },
                },
              }}
              slotProps={{
                monthButton: {
                  onDoubleClickCapture: (event) => handleDateDoubleClick(event),
                },
              }}
            />
          </Box>
        </Popper>
      </LocalizationProvider>
    );
  }
);

export default SnPeriodMonthBox;
